import { useEffect, useState } from "react";

// Keeps the screen on while the calling component is mounted (the cooking
// layout), using the Screen Wake Lock API. The browser drops the lock when
// the tab is hidden, so it is asked for again on return. Returns whether a
// lock is held right now: false where the API is missing (older browsers)
// or the request is refused (e.g. battery saver).
export function useWakeLock(): boolean {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!("wakeLock" in navigator)) return undefined;
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;

    async function request() {
      try {
        const next = await navigator.wakeLock.request("screen");
        if (cancelled) {
          next.release().catch(() => {});
          return;
        }
        lock = next;
        setActive(true);
        next.addEventListener("release", () => setActive(false));
      } catch {
        setActive(false);
      }
    }
    const onVisibility = () => {
      if (document.visibilityState === "visible") request();
    };

    request();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      lock?.release().catch(() => {});
    };
  }, []);

  return active;
}
